/* eslint-disable no-underscore-dangle */
import {
  Connector,
  InstantSearch,
  RendererOptions,
  Widget,
  WidgetDescription,
} from 'instantsearch.js';
import { inject, onBeforeUnmount, Ref, ref, unref, watch } from 'vue';

export interface SSRRenderer {
  __forceRender?: (widget: Widget, parent: InstantSearch['mainIndex']) => void;
}

export const useWidget = <
  TWidgetDescription extends WidgetDescription,
  TWidgetParams extends object,
>(
  config: { connector: Connector<TWidgetDescription, TWidgetParams> },
  widgetParams: Ref<TWidgetParams>,
  additionalProperties?: object,
) => {
  const instantSearchInstance = inject<InstantSearch & SSRRenderer>(
    '$_ais_instantSearchInstance',
    // @ts-expect-error
    () => {
      // TODO: `getCurrentInstance()?.ctx.$options.name` for tag name
      // throw new TypeError('It looks like you forgot to wrap your Algolia search component "<${tag}>" inside of an "<ais-instant-search>" component.');
      throw new TypeError(
        'It looks like you forgot to wrap your Algolia search component inside of an "<ais-instant-search>" component.',
      );
    },
  );
  const getParentIndex = inject('$_ais_getParentIndex', () => instantSearchInstance.mainIndex);

  const factory = ref<ReturnType<typeof config.connector>>();
  const state = ref<TWidgetDescription['renderState'] & RendererOptions<TWidgetParams>>();
  const widget = ref<Widget>();

  const updateState = (
    widgetState?: TWidgetDescription['renderState'] & RendererOptions<TWidgetParams>,
    isFirstRender?: boolean,
  ) => {
    if (!isFirstRender) {
      state.value = widgetState ?? undefined;
    }
  };

  if (typeof config.connector === 'function') {
    factory.value = config.connector(updateState, () => {});
    widget.value = {
      ...factory.value(unref(widgetParams)),
      ...additionalProperties,
    };
    getParentIndex().addWidgets([widget.value!]);

    if (instantSearchInstance._initialResults && !instantSearchInstance.started) {
      if (typeof instantSearchInstance.__forceRender !== 'function') {
        throw new Error(
          'You are using server side rendering with <ais-instant-search> instead of <ais-instant-search-ssr>.',
        );
      }
      instantSearchInstance.__forceRender(widget.value!, getParentIndex());
    }
  } else if (config.connector !== true) {
    // eslint-disable-next-line no-console
    console.warn(
      `You are using the InstantSearch widget mixin, but didn't provide a connector.
While this is technically possible, and will give you access to the Helper,
it's not the recommended way of making custom components.
If you want to disable this message, pass { connector: true } to the mixin.
Read more on using connectors: https://alg.li/vue-custom`,
    );
  }

  onBeforeUnmount(() => {
    if (widget.value) {
      getParentIndex().removeWidgets([widget.value]);
    }
  });

  watch(
    widgetParams,
    (nextWidgetParams) => {
      state.value = undefined;
      getParentIndex().removeWidgets([widget.value!]);
      widget.value = {
        ...factory.value!(nextWidgetParams),
        ...additionalProperties,
      };
      getParentIndex().addWidgets([widget.value!]);
    },
    { deep: true },
  );

  return {
    state,
  };
};
